<template>
  <v-container class="page-container">
    <StoreLocatorConfigEmpty
      v-if="(!isStoreLocatorEnabled || noConfig) && !isLoading"
    />
    <v-row justify="center" v-else-if="config && !noConfig">
      <ViewHeader :title="viewTitle" />
      <v-col cols="12">
        <v-tabs
          v-model="active"
          slider-color="primary"
          class="page-container__tabs"
        >
          <v-tab
            v-for="tab in tabsWithRestrictions"
            :key="tab.id"
            :to="tab.route"
            class="page-container__tab"
          >
            <v-icon size="21px">
              {{ tab.icon }}
            </v-icon>
            <span>
              {{ $t(tab.label) }}
            </span>
          </v-tab>
        </v-tabs>
        <v-card v-if="config" class="page-container__card">
          <v-tabs-items v-model="active" @change="updateRouter($event)">
            <v-row no-gutters justify="center">
              <v-col>
                <transition name="fade" mode="out-in">
                  <router-view
                    class="animation-duration--500"
                    :config="config"
                    @config-saved="fetchConfig"
                  ></router-view>
                </transition>
              </v-col>
            </v-row>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
    <SectionLoader v-if="isLoading" status :opacity="1" />
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader.vue";
import cuid from "cuid";
import serviceHandler from "@/helpers/handlers/service";
import storeLocator from "@/services/storeLocator";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import StoreLocatorConfigEmpty from "@/components/ConfigEmpty/StoreLocatorConfigEmpty.vue";

const StoreLocatorTabs = {
  CONFIG: "config",
  FAQ: "faq"
};

export default {
  name: "StoreLocator",
  components: {
    ViewHeader,
    SectionLoader,
    StoreLocatorConfigEmpty
  },
  data() {
    return {
      viewTitle: this.$t("message.storeLocator.title"),
      active: 0,
      isLoading: true,
      tabs: [
        {
          id: StoreLocatorTabs.CONFIG,
          label: "message.storeLocator.general",
          route: { name: "store-locator-general-config" },
          icon: "mdi-folder"
        },
        {
          id: StoreLocatorTabs.FAQ,
          label: "message.storeLocator.faqTitle",
          route: { name: "store-locator-faq" },
          icon: "mdi-help-circle"
        }
      ],
      config: null
    };
  },
  computed: {
    isStoreLocatorEnabled() {
      return this.$store.state.company.company.enabled_store_locator;
    },
    isFaqEnabled() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_6573_FAQ_STORE_LOCATOR);
    },
    tabsWithRestrictions() {
      if (!this.isFaqEnabled) {
        return this.tabs.filter(tab => tab.id !== StoreLocatorTabs.FAQ);
      }
      return this.tabs;
    },
    noConfig() {
      return !this.config;
    }
  },
  async created() {
    await this.fetchConfig();
  },
  methods: {
    async fetchConfig() {
      this.config = null;
      this.isLoading = true;
      this.config = (await serviceHandler(storeLocator.getConfig()))[0] || null;
      this.isLoading = false;
    },

    updateRouter(val) {
      this.$router.push(val);
    }
  }
};
</script>

<style lang="scss" scoped>
.page-container {
  ::v-deep .v-tabs-bar__content {
    margin-top: 10px;
    padding-left: 10px;
    height: 48px;
  }
  ::v-deep * {
    overflow: visible !important;
  }
  &__tabs {
    margin-top: -10px;
    margin-left: -10px;
    margin-bottom: 10px;
    position: relative;
    z-index: 1;
    ::v-deep .v-tabs-bar {
      background: none;
    }

    ::v-deep .v-tabs-slider-wrapper {
      display: none;
    }

    ::v-deep .v-slide-group__wrapper {
      height: 58px;
    }
  }

  &__tab {
    text-transform: none;
    letter-spacing: normal;
    font-size: 16px;
    font-weight: 500;
    padding: 0 20px;
    background: $gray-300;
    @include font-size(14px);
    display: flex;
    gap: $spacing;
    height: 48px;
    padding-left: $spacing * 3;
    padding-right: $spacing * 3;

    &,
    &:before {
      border-top-right-radius: 16px;
      border-top-left-radius: 16px;
    }

    &:not(.v-tab--active) {
      &,
      .v-icon {
        color: $grey-darker !important;
      }
    }

    &.v-tab--active {
      background: #fff;
      filter: drop-shadow(0px 0px 10px rgba(54, 60, 86, 0.07));
    }
  }

  &__card {
    ::v-deep &.v-card {
      border-top-left-radius: 0;
    }
  }
}
</style>
